import countries from "./assets/countries.json";
import EURIcon from "./assets/currencies/eur.png";
import GBPIcon from "./assets/currencies/gbp.png";
import INRIcon from "./assets/currencies/inr.png";
import PHPIcon from "./assets/currencies/php.png";
import PKRIcon from "./assets/currencies/pkr.png";
import USDTIcon from "./assets/currencies/usdt.png";
import USDIcon from "./assets/currencies/usd.png";
import USDCIcon from "./assets/currencies/usdc.png";
import { StatusSet, UIStatus, UIStatusProps } from "./types/GlobalTypes";

import {
  Currency,
  CurrencyType,
  CurrencyDetails,
  InvoiceStatus,
  IncomingPaymentDetails,
} from "./types/InvoiceTypes";

import { AccountType, PhoneChannel } from "./types/UserTypes";
import { Bank, Chain, ChainDetails, ChainType } from "./types/WalletTypes";
import { PayerRelationshipEnum } from "./types/PaymentRequestTypes";

export const ENV = process.env.NODE_ENV || "development";
export const API_URL =
  process.env.REACT_APP_API_SERVER || "http://localhost:8000/api";
export const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID || "";
export const GTM_ID = process.env.REACT_APP_GTM_ID || "";
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || "";
export const ONBOARDING_SURVEY_ID =
  process.env.REACT_APP_ONBOARDING_SURVEY_ID || undefined;
export const PRODUCT_FRUITS_WORKSPACE_CODE =
  process.env.REACT_APP_PRODUCT_FRUITS_WORKSPACE_CODE || "";
export const PRODUCT_FRUITS_LANGUAGE = "en";

export const ENABLE_MOCKS = {
  testHandlers: true,
};

export const TOKEN_KEY = "arcUserToken";

export const COOKIES_DOMAIN = ".archway.finance";

export const SUPPORT_EMAIL = "support@archway.finance";

export const ACCOUNT_TYPE_STRING = {
  [AccountType.CONTRACTOR]: "contractor",
  [AccountType.BUSINESS]: "business",
};

export const ACCOUNT_TYPE_ROUTE_VALUES = {
  individual: AccountType.CONTRACTOR,
  company: AccountType.BUSINESS,
};

export const PHONE_CHANNEL_LABELS = {
  [PhoneChannel.SMS]: "SMS",
  [PhoneChannel.WHATSAPP]: "WhatsApp",
};

export const PHONE_CHANNEL_OPTIONS = [
  { value: PhoneChannel.SMS, label: PHONE_CHANNEL_LABELS[PhoneChannel.SMS] },
  {
    value: PhoneChannel.WHATSAPP,
    label: PHONE_CHANNEL_LABELS[PhoneChannel.WHATSAPP],
  },
];

export const INVOICE_STATUS_LABEL = {
  [InvoiceStatus.DRAFT]: "Draft",
  [InvoiceStatus.PENDING]: "Pending payment",
  [InvoiceStatus.PROCESSING]: "Processing",
  [InvoiceStatus.PENDING_REVIEW]: "Pending review",
  [InvoiceStatus.PAID]: "Paid",
  [InvoiceStatus.CANCELLED]: "Cancelled",
};

export const INVOICE_STATUS_SHORT_LABEL = {
  [InvoiceStatus.PENDING]: "Pending",
};

export const DEFAULT_FIAT_CURRENCY = Currency.EUR;

export const DEFAULT_CRYPTO_CURRENCY = Currency.USDC;

export const CURRENCY_MAP: Record<Currency, CurrencyDetails> = {
  [Currency.USDT]: {
    type: CurrencyType.CRYPTO,
    value: Currency.USDT,
    code: Currency.USDT.toUpperCase(),
    name: "Tether USD",
    symbol: "USDT",
    alternativeSymbol: "USD(T)",
    features: ["crypto_chain", "crypto_address"],
    decimals: 6,
    icon: USDTIcon,
    supportedChains: [Chain.Ethereum, Chain.Tron, Chain.Polygon],
  },
  [Currency.USDC]: {
    type: CurrencyType.CRYPTO,
    value: Currency.USDC,
    code: Currency.USDC.toUpperCase(),
    name: "USD Coin",
    symbol: "USDC",
    alternativeSymbol: "USD(C)",
    features: ["crypto_chain", "crypto_address"],
    decimals: 6,
    icon: USDCIcon,
    supportedChains: [Chain.Polygon, Chain.Ethereum],
  },
  [Currency.EUR]: {
    type: CurrencyType.FIAT,
    value: Currency.EUR,
    code: Currency.EUR.toUpperCase(),
    name: "Euro",
    symbol: "€",
    features: ["beneficiary_name", "iban", "reference", "bank_name", "bic"],
    decimals: 2,
    icon: EURIcon,
    supportedChains: [Chain.Euro],
  },
  [Currency.GBP]: {
    type: CurrencyType.FIAT,
    value: Currency.GBP,
    code: Currency.GBP.toUpperCase(),
    name: "UK Pound",
    symbol: "£",
    features: [
      "beneficiary_name",
      "account_number",
      "reference",
      "bank_name",
      "bank_code",
      "bank_code_sort_code",
    ],
    decimals: 2,
    icon: GBPIcon,
  },
  [Currency.USD]: {
    type: CurrencyType.FIAT,
    value: Currency.USD,
    code: Currency.USD.toUpperCase(),
    name: "US Dollar",
    symbol: "$",
    features: [
      "beneficiary_name",
      "beneficiary_address",
      "bic",
      "bank_code",
      "bank_code_ach",
      "bank_code_fed",
      "bank_name",
      "bank_address",
      "account_number",
      "reference",
    ],
    decimals: 2,
    icon: USDIcon,
  },
  [Currency.INR]: {
    type: CurrencyType.FIAT,
    value: Currency.INR,
    code: Currency.INR.toUpperCase(),
    name: "Indian Rupee",
    symbol: "₹",
    features: ["beneficiary_name", "account_number", "reference", "bank_code"],
    decimals: 2,
    icon: INRIcon,
    supportedChains: [Chain.INR],
  },
  [Currency.PHP]: {
    type: CurrencyType.FIAT,
    value: Currency.PHP,
    code: Currency.PHP.toUpperCase(),
    name: "Philippine Peso",
    symbol: "₱",
    features: [
      "beneficiary_name",
      "account_number",
      "reference",
      "bank_name",
      "bank_code",
    ],
    decimals: 2,
    icon: PHPIcon,
    supportedChains: [Chain.PHP],
  },
  [Currency.PKR]: {
    type: CurrencyType.FIAT,
    value: Currency.PKR,
    code: Currency.PKR.toUpperCase(),
    name: "Pakistan Rupee",
    symbol: "₨",
    features: [
      "beneficiary_name",
      "account_number",
      "reference",
      "bank_name",
      "bank_code",
    ],
    decimals: 2,
    icon: PKRIcon,
    supportedChains: [Chain.PKR],
  },
};

export const CURRENCIES: CurrencyDetails[] = Object.values(CURRENCY_MAP);

export const CHAINS_MAP: Record<Chain, ChainDetails> = {
  [Chain.Tron]: {
    name: "Tron",
    value: Chain.Tron,
    type: ChainType.CRYPTO,
    tokenStandard: "TRC-20",
  },
  [Chain.Ethereum]: {
    name: "Ethereum",
    value: Chain.Ethereum,
    type: ChainType.CRYPTO,
    tokenStandard: "ERC-20",
  },
  [Chain.Polygon]: {
    name: "Polygon",
    value: Chain.Polygon,
    type: ChainType.CRYPTO,
    tokenStandard: "ERC-20",
  },
  [Chain.Euro]: {
    name: "Euro",
    value: Chain.Euro,
    type: ChainType.FIAT,
  },
  [Chain.INR]: {
    name: "Indian Rupee",
    value: Chain.INR,
    type: ChainType.FIAT,
    features: ["bank_code"],
  },
  [Chain.PHP]: {
    name: "Philippine Peso",
    value: Chain.PHP,
    type: ChainType.FIAT,
  },
  [Chain.PKR]: {
    name: "Pakistan Rupee",
    value: Chain.PKR,
    type: ChainType.FIAT,
  },
};

export const CHAINS = Object.values(CHAINS_MAP);

export const TERMS_LINKS = {
  terms: "https://archway.finance/terms-of-service",
  privacy: "https://archway.finance/privacy-policy",
};

export const FOOTER_LINKS = [
  {
    text: "Archway.Finance",
    href: "https://archway.finance/",
    className: "",
  },
  {
    text: "Terms & Conditions",
    href: TERMS_LINKS.terms,
    className: "",
  },
  {
    text: "Privacy Policy",
    href: TERMS_LINKS.privacy,
    className: "",
  },
  {
    text: "Contact Support",
    href: "mailto:support@archway.finance",
    className: "contact-support-btn",
  },
];

type IncomingPaymentDetailsConfigProps = {
  label: string;
  labelClassName?: string;
  format?: (value?: string) => string;
  formatPDF?: (value?: string) => string | string[];
  valueClassName?: string;
  alwaysVisible?: boolean;
  hideByDefault?: boolean;
  pdfLabelClassName?: string;
  pdfValueClassName?: string;
};

export const INCOMING_PAYMENT_DETAILS_CONFIG: Record<
  keyof IncomingPaymentDetails,
  IncomingPaymentDetailsConfigProps
> = {
  beneficiary_name: { label: "Recipient" },
  iban: { label: "IBAN" },
  account_number: { label: "Account #" },
  reference: {
    label: "REF # / Memo / Comment",
    labelClassName: "text-red-600",
    valueClassName: "text-red-600",
    pdfLabelClassName: "textRed",
    pdfValueClassName: "textRed",
  },
  bank_name: { label: "Bank" },
  bic: { label: "Swift" },
  bank_code: { label: "Bank Code", hideByDefault: true },
  bank_code_sort_code: { label: "Sort Code", hideByDefault: true },
  bank_code_ach: { label: "ACH Routing #", hideByDefault: true },
  bank_code_fed: { label: "FEDWIRE Routing #", hideByDefault: true },
  crypto_chain: {
    label: "Chain",
    format: (value?: string) => {
      if (!value) return "";
      const { name, tokenStandard } = CHAINS_MAP[value as Chain];
      return `${name} (${tokenStandard})`;
    },
  },
  crypto_address: { label: "Address" },
  beneficiary_address: { label: "Beneficiary Address" },
  bank_address: { label: "Bank Address" },
};

export type StatusConfigProps = {
  icon: string;
  text: string;
  shortText?: string;
  color: string;
  key?: string;
  order?: number;
};

export const STATUS_CONFIG: Record<
  StatusSet,
  Record<number | string, StatusConfigProps>
> = {
  [StatusSet.INVOICE]: {
    DRAFT: {
      icon: "Edit",
      text: INVOICE_STATUS_LABEL[InvoiceStatus.DRAFT],
      color: "text-gray-500",
      key: "draft",
      order: 10,
    },
    PENDING: {
      icon: "hourglass_empty",
      text: INVOICE_STATUS_LABEL[InvoiceStatus.PENDING],
      shortText: INVOICE_STATUS_SHORT_LABEL[InvoiceStatus.PENDING],
      color: "text-purple-700",
      key: "ready",
      order: 20,
    },
    PROCESSING: {
      icon: "hourglass_bottom",
      text: INVOICE_STATUS_LABEL[InvoiceStatus.PROCESSING],
      color: "text-purple-700",
      key: "processing",
      order: 30,
    },
    PENDING_REVIEW: {
      icon: "hourglass_top",
      text: INVOICE_STATUS_LABEL[InvoiceStatus.PENDING_REVIEW],
      color: "text-purple-700",
      key: "pending_review",
      order: 40,
    },
    PAID: {
      icon: "Done",
      text: INVOICE_STATUS_LABEL[InvoiceStatus.PAID],
      color: "text-green-500",
      key: "paid",
      order: 50,
    },
    CANCELLED: {
      icon: "Cancel",
      text: INVOICE_STATUS_LABEL[InvoiceStatus.CANCELLED],
      color: "!text-orange-500", // Added ! to apply important in the case of avatar icons
      key: "cancelled",
      order: 60,
    },
  },
  [StatusSet.CONTACT]: {
    Verified: {
      icon: "Done",
      text: "Verified",
      color: "text-green-500",
    },
    Pending: {
      icon: "Timer",
      text: "Pending",
      color: "text-brown-500",
    },
    Expired: {
      icon: "Cancel",
      text: "Expired",
      color: "text-red-500",
    },
  },
};

export { countries };

const countriesBlacklist = ["US", "CA", "IR", "CU", "KP", "SY", "CN"];

const bankAccountSupportedCountries = ["IN", "PH", "PK"];

export const whitelistedCountries = countries.filter(
  ({ value }) => !countriesBlacklist.includes(value)
);

export const bankAccountWhitelistedCountries = countries.filter(({ value }) =>
  bankAccountSupportedCountries.includes(value)
);

export const BANKS: Bank[] = [
  // India
  {
    name: "BANK OF INDIA",
    id: "INDBAN",
    chain: Chain.INR,
    codes: ["KKBK000123", "BKID0007114"],
  },
  {
    name: "HDFC BANK",
    id: "INDHDF",
    chain: Chain.INR,
    codes: ["HDFC0000123"],
  },
  { name: "INDICI BANK", id: "INDICI", chain: Chain.INR, codes: [] },
  {
    name: "PUNJAB NATIONAL BANK",
    id: "1796",
    chain: Chain.INR,
    codes: ["PUNB0783800"],
  },
  // Phillipines
  { name: "ALLBANK INC", id: "PHLALL", chain: Chain.PHP },
  { name: "ASIA UNITED BANK", id: "PHLASI", chain: Chain.PHP },
  { name: "BANGKOK BANK PUBLIC CO., LTD.", id: "PHLBAN01", chain: Chain.PHP },
  { name: "BANK OF THE PHILIPPINE ISLANDS", id: "PHLBANK", chain: Chain.PHP },
  { name: "BDO", id: "PHLBDO2117", chain: Chain.PHP },
  { name: "RCBC BANK", id: "PHLRCB159", chain: Chain.PHP },
  // Pakistan
  { name: "AL BARAKA BANK (PAKISTAN) LTD", id: "PAKAL", chain: Chain.PKR },
  { name: "ALLIED BANK LIMITED", id: "PAKALL", chain: Chain.PKR },
  { name: "APNA BANK", id: "PAKAPN", chain: Chain.PKR },
  { name: "ASKARI BANK", id: "PAKASK", chain: Chain.PKR },
  { name: "BANK AL HABIB", id: "PAKALH", chain: Chain.PKR },
  { name: "BANK ALFALAH", id: "PAKALF", chain: Chain.PKR },
  { name: "BANK ISLAMI PAKISTAN LIMITED", id: "PAKISL", chain: Chain.PKR },
  { name: "BANK OF PUNJAB", id: "PAKBAN", chain: Chain.PKR },
  { name: "DEUTSCHE BANK", id: "PAKDEU", chain: Chain.PKR },
  { name: "DUBAI ISLAMIC BANK", id: "PAKDUB", chain: Chain.PKR },
  { name: "FAISAL BANK", id: "PAKFAI", chain: Chain.PKR },
  { name: "FIRST WOMEN BANK", id: "PAKFIR", chain: Chain.PKR },
  { name: "HABIB BANK LIMITED", id: "PAKHAB", chain: Chain.PKR },
  { name: "HABIB METRO POLITIAN BANK", id: "PAKHAP", chain: Chain.PKR },
  {
    name: "INDUSTRIAL AND COMMERCIAL BANK OF CHINA LIMITED",
    id: "PAKIND",
    chain: Chain.PKR,
  },
  { name: "JS BANK", id: "PAKJS", chain: Chain.PKR },
  { name: "MCB ISLAMIC BANK", id: "PAKMCB", chain: Chain.PKR },
  { name: "MEEZAN BANK", id: "PAKMEE", chain: Chain.PKR },
  { name: "MOBILINK MICROFINANCE BANK", id: "PAKMOB", chain: Chain.PKR },
  { name: "MUSLIM COMMERCIAL BANK", id: "PAKMUS", chain: Chain.PKR },
  { name: "NATIONAL BANK OF PAKISTAN", id: "PAKNAT", chain: Chain.PKR },
  { name: "OMAN INTERNATIONAL", id: "PAKOMA", chain: Chain.PKR },
  { name: "SADAPAY", id: "PAKSAD", chain: Chain.PKR },
  { name: "SAMBA BANK", id: "PAKSAM", chain: Chain.PKR },
  { name: "SILK BANK", id: "PAKSIL", chain: Chain.PKR },
  { name: "SINDH BANK", id: "PAKSIN", chain: Chain.PKR },
  { name: "SME BANK LTD", id: "PAKSME", chain: Chain.PKR },
  { name: "SONERI BANK LIMITD", id: "PAKSON", chain: Chain.PKR },
  { name: "STANDARD CHARTERD BANK", id: "PAKSTA", chain: Chain.PKR },
  { name: "SUMMIT BANK LIMITED", id: "PAKSUM", chain: Chain.PKR },
  {
    name: "TAMEER MICROFINANCE BANK LTD / EASYPAISA / TELENOR MICROFINANCE BANK",
    id: "PAKTAM",
    chain: Chain.PKR,
  },
  { name: "THE BANK OF KHYBER", id: "PAKTHE", chain: Chain.PKR },
  { name: "TOKYO MITSUBISHI", id: "PAKTOK", chain: Chain.PKR },
  { name: "U MICROFINANCE BANK", id: "PAKU M", chain: Chain.PKR },
  { name: "UNITED BANK LIMITED", id: "PAKUNI", chain: Chain.PKR },
  { name: "ZARAI TARAQIATI BANK LTD", id: "PAKZAR", chain: Chain.PKR },
];

export const BANKS_MAP: Record<string, Bank> = BANKS.reduce(
  (acc, bank) => ({ ...acc, [bank.id]: bank }),
  {}
);

export const uiConstants: Record<UIStatus, UIStatusProps> = {
  [UIStatus.OK]: {
    backgroundColor: "bg-gray-50",
    borderColor: "border-gray-500",
    fillColor: "bg-gray-500",
    strippedFill: "progress-bar-stripped-ok",
  },
  [UIStatus.SUCCESS]: {
    backgroundColor: "bg-green-50",
    borderColor: "border-green-300",
    fillColor: "bg-green-500",
  },
  [UIStatus.WARNING]: {
    backgroundColor: "!bg-brown-100",
    borderColor: "!border-brown-300",
    textColor: "text-brown-500",
    fillColor: "bg-brown-500",
  },
  [UIStatus.ERROR]: {
    backgroundColor: "!bg-orange-50",
    borderColor: "!border-orange-300",
    fillColor: "bg-orange-500",
    textColor: "text-orange-500",
    strippedFill: "progress-bar-stripped-error",
  },
  [UIStatus.NEUTRAL]: {
    backgroundColor: "bg-slate-100",
    borderColor: "border-slate-400",
    fillColor: "bg-slate-500",
  },
  [UIStatus.ATTENTION]: {
    backgroundColor: "bg-brown-50",
    borderColor: "border-brown-300",
    fillColor: "bg-brown-500",
  },
};

type PayerRelationshipConfig = {
  label: string;
  value: PayerRelationshipEnum;
};

export const PAYER_RELATIONSHIP_CONFIG: PayerRelationshipConfig[] = [
  {
    value: PayerRelationshipEnum.BUSINESS_EXPENSES_AND_INVESTMENTS,
    label: "Business payment, expenses, or investments",
  },
  { value: PayerRelationshipEnum.ME, label: "Myself" },
  {
    value: PayerRelationshipEnum.FRIENDS_AND_FAMILY,
    label: "Friends or family",
  },
  {
    value: PayerRelationshipEnum.MERCHANT_AND_CUSTOMER,
    label: "Merchant / customer",
  },
  { value: PayerRelationshipEnum.OTHER, label: "Other" },
];
